import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, onMounted, watch } from "vue";
import { storeToRefs } from "pinia";
import { useEUAccountsStore } from '@/store_v3/EUAccountsStore';
import { getAdvertisersSearch, getLegalEntities, getOneAdvertiser } from "@/api/revenue";
import { IAdvertiser, IContractor, ILegalEntityOrg } from "@/api/types/revenue";
import { USER_ROLES } from "@/mappings/user-roles";
import { showServerError } from "@/utils";
import store from "@/store";
import BaseList from "@/components-UI/list/BaseList.vue";
import BaseListItem from "@/components-UI/list/BaseListItem.vue";
import BaseAutocomplete from "@/components-UI/BaseAutocomplete.vue";
import BaseSelect from "@/components-UI/BaseSelect.vue";
import BaseInputNumber from "@/components-UI/BaseInputNumber.vue";
import BaseTextarea from "@/components-UI/BaseTextarea.vue";
import ViewDateTime from "@/components/base/ViewDateTime.vue";
import BaseChip from "@/components-UI/BaseChip.vue";
import BaseEditableCard from "@/components-UI/BaseEditableCard.vue";
import BaseMonthPicker from "@/components-UI/BaseMonthPicker.vue";
import router from "@/router";


export default /*#__PURE__*/_defineComponent({
  __name: 'ComponentList',
  setup(__props) {

const EUAccountStore = useEUAccountsStore();
const {
    isLoading,
    form,
    selectedAdvertiser,
    EUAccountObj,
    isEdit,
} = storeToRefs(EUAccountStore);
const {
    recountSumIfChangeCurrency,
    clearAll,
} = EUAccountStore;

const currencyItems = ref<{ value: string }[]>([]);
const organizationsItems = ref<ILegalEntityOrg[]>([]);
const contractorsItems = ref<IContractor[]>([]);
const projectsItems = ref<{ value: string }[]>([]);

const getDaysToPay = computed(() => {
    const num = EUAccountObj.value?.days_to_pay;
    let dayText = 'дней';

    if (num === 1) dayText = 'день';
    if (num > 1 && num < 5) dayText = 'дня';

    return `${num} ${dayText}`;
});

const isSingle = computed(() => router.currentRoute.params.id !== 'create');

/**
 * Все евросчета сейчас выставляются на одну организацию, поэтому на создании
 * она подставляется сама, а поле блокируется. Появится второе юрлицо - убрать
 * подстановку и вернуть выбор.
 */
const DEFAULT_LEGAL_ENTITY_NAME = 'SMART CITY OÜ';

/** Организация по умолчанию, если она есть в справочнике этого окружения. */
const defaultLegalEntity = computed<ILegalEntityOrg | null>(() =>
    organizationsItems.value.find(
        (org) => org.name?.trim().toLowerCase() === DEFAULT_LEGAL_ENTITY_NAME.toLowerCase(),
    ) ?? null,
);

/**
 * Поле блокируется только когда организацию действительно удалось подставить:
 * иначе счёт нельзя было бы создать вовсе.
 */
const isLegalEntityLocked = computed<boolean>(() => !isSingle.value && !!defaultLegalEntity.value);

/**
 * Держим организацию выставленной на создании счёта: справочник приходит запросом,
 * а `clearAll` при очистке рекламодателя обнуляет форму - вернуть значение руками
 * нельзя, поле заблокировано.
 */
watch([defaultLegalEntity, () => form.value.legal_entity_id], () => {
    const defaultId = defaultLegalEntity.value?.id;
    if (isSingle.value || defaultId == null) {
        return;
    }
    if (form.value.legal_entity_id !== Number(defaultId)) {
        form.value.legal_entity_id = Number(defaultId);
    }
}, { immediate: true });

const isDisabledMainCardFields = computed<boolean>(() => {
    const currentUser = store.getters['authModule/GET_USER'];

    if (!currentUser || !currentUser.roles || currentUser.roles.length === 0) {
        return true;
    }

    const userRoles = currentUser.roles as string[];
    const userAccountId = currentUser.account_id;

    if (userRoles.includes(USER_ROLES.ADMIN) || userRoles.includes(USER_ROLES.SUPERVISOR)) {
        return false;
    }

    if (userRoles.includes(USER_ROLES.ACCOUNT_MANAGER)) {
        if (!selectedAdvertiser.value || !userAccountId) {
            return true;
        }

        const isAccountManager = selectedAdvertiser.value.account_manager?.account_id === userAccountId;
        const isAdditionalManager = selectedAdvertiser.value.managers?.some((manager) =>
            manager.manager_id === userAccountId,
        ) || false;

        return !(isAccountManager || isAdditionalManager);
    }

    return true;
});

const isAdvertiserFieldDisabled = computed<boolean>(() => {
    const currentUser = store.getters['authModule/GET_USER'];

    if (!currentUser || !currentUser.roles || currentUser.roles.length === 0) {
        return true;
    }

    const userRoles = currentUser.roles as string[];
    if (userRoles.includes(USER_ROLES.ADMIN) || userRoles.includes(USER_ROLES.SUPERVISOR)) {
        return false;
    }

    if (userRoles.includes(USER_ROLES.ACCOUNT_MANAGER)) {
        return false;
    }

    return true;
});

const handleSelectValue = async ({ item }: { item: IAdvertiser }): Promise<void> => {
    if (!item) return;

    form.value.advertiser_id = item.id;

    try {
        const fullAdvertiser = await getOneAdvertiser(item.id);
        selectedAdvertiser.value = fullAdvertiser;

        currencyItems.value = fullAdvertiser.currency?.length ? fullAdvertiser.currency.map(e => ({ value: e })) : [];
        contractorsItems.value = fullAdvertiser.contractors ?? [];
        projectsItems.value = fullAdvertiser.projects?.map(e => ({ value: e })) ?? [];

        if (!!fullAdvertiser.currency?.length && fullAdvertiser.currency.length === 1) {
            form.value.currency = fullAdvertiser.currency[0];
        }

        if (!!fullAdvertiser.contractors?.length && fullAdvertiser.contractors.length === 1) {
            form.value.contractor_id = fullAdvertiser.contractors[0].id;
            handleUpdateContractor(fullAdvertiser.contractors[0]);
        }

        if (!!fullAdvertiser.projects?.length && fullAdvertiser.projects.length === 1) {
            form.value.project = fullAdvertiser.projects[0];
        }
    } catch (e) {
        showServerError(e, 'Не удалось получить данные рекламодателя');
        clearAll();
    }
};

const handleUpdateContractor = (item: IContractor): void => {
    form.value.contractor_id = item.id;

    if (!form.value.days_to_pay && !!item.days_to_pay) {
        form.value.days_to_pay = item.days_to_pay;
    }
};

watch(() => form.value.advertiser_id, async (): Promise<void> => {
    if (isSingle.value && form.value.advertiser_id) {
        try {
            selectedAdvertiser.value = await getOneAdvertiser(form.value.advertiser_id);
            currencyItems.value = selectedAdvertiser.value?.currency.map(el => ({ value: el })) || [];
            projectsItems.value = selectedAdvertiser.value?.projects.map(el => ({ value: el })) || [];
            contractorsItems.value = selectedAdvertiser.value.contractors;
        } catch (e) {
            showServerError(e, 'Не удалось получить рекламодателя');
        }
    }
}, { immediate: true });

onMounted(async () => {
    try {
        isLoading.value.main = true;
        const response = await getLegalEntities();
        organizationsItems.value = response.data;
    } catch (e) {
        showServerError(e, 'Список организаций не загружен');
    } finally {
        isLoading.value.main = false;
    }
});

return { __sfc: true,EUAccountStore, isLoading, form, selectedAdvertiser, EUAccountObj, isEdit, recountSumIfChangeCurrency, clearAll, currencyItems, organizationsItems, contractorsItems, projectsItems, getDaysToPay, isSingle, DEFAULT_LEGAL_ENTITY_NAME, defaultLegalEntity, isLegalEntityLocked, isDisabledMainCardFields, isAdvertiserFieldDisabled, handleSelectValue, handleUpdateContractor, getAdvertisersSearch, BaseList, BaseListItem, BaseAutocomplete, BaseSelect, BaseInputNumber, BaseTextarea, ViewDateTime, BaseChip, BaseEditableCard, BaseMonthPicker }
}

})